/**
 * Unified Business API Client Layer
 *
 * DRY-01.1: Consolidated frontend API client with:
 *   - Structured error handling
 *   - Automatic tenant isolation
 *   - Full TypeScript typing
 *   - Request/response schemas from canonical types
 *   - Memoization-ready for referential stability
 *
 * This module re-exports the `createEntityService` factory and provides
 * typed API endpoints for all business entities. Components import from
 * this single entry point instead of raw axios/fetch calls.
 *
 * Pattern:
 *   - All endpoints typed with request/response schemas
 *   - Every request automatically includes tenant context
 *   - Errors return structured { code, message, details } format
 *   - CRUD endpoints generated via createEntityService factory
 *   - No component-level HTTP calls — service layer abstraction enforced
 *
 * Usage:
 *   import { businessApi } from '@/shared/api/businessApi';
 *
 *   // Type-safe CRUD operations with auto tenant isolation
 *   const customers = await businessApi.customers.list();
 *   const customer = await businessApi.customers.get(id);
 *   const newCustomer = await businessApi.customers.create({ name: '...' });
 */

import { createEntityService, type EntityService, type PaginatedResponse } from '../services';

// ─── Entity Type Definitions (from canonical schemas) ─────────────────────

/**
 * Customer entity with full business context.
 * Mirrors backend Customer model schema.
 */
export interface Customer {
  id?: number;
  name: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  customer_type?: 'restaurant' | 'retail' | 'distributor' | 'food_service' | 'other';
  is_active?: boolean;
  credit_limit?: number;
  payment_terms?: string;
  created_on?: string;
  modified_on?: string;
}

/**
 * Supplier entity with operational details.
 * Mirrors backend Supplier model schema.
 */
export interface Supplier {
  id?: number;
  name: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  supplier_type?: 'meat_processor' | 'packaging' | 'equipment' | 'logistics' | 'other';
  is_active?: boolean;
  created_on?: string;
  modified_on?: string;
}

/**
 * Contact entity for people management.
 * Mirrors backend Contact model schema.
 */
export interface Contact {
  id?: number;
  name: string;
  email?: string;
  phone?: string;
  position?: string;
  company?: string;
  notes?: string;
  is_active?: boolean;
  contact_type?: 'supplier' | 'customer' | 'internal' | 'other';
  created_on?: string;
  modified_on?: string;
}

/**
 * Purchase order entity tracking supplier transactions.
 * Mirrors backend PurchaseOrder model schema.
 */
export interface PurchaseOrder {
  id?: number;
  po_number: string;
  supplier?: number;
  order_date?: string;
  delivery_date?: string;
  status?: 'draft' | 'pending' | 'approved' | 'ordered' | 'received' | 'cancelled';
  total_amount?: number;
  notes?: string;
  created_by?: number;
  created_on?: string;
  modified_on?: string;
}

/**
 * Accounts receivable entity tracking customer invoices.
 * Mirrors backend AccountsReceivable model schema.
 */
export interface AccountsReceivable {
  id?: number;
  invoice_number: string;
  customer?: number;
  amount: number;
  due_date?: string;
  status?: 'pending' | 'paid' | 'overdue' | 'cancelled';
  description?: string;
  created_on?: string;
  modified_on?: string;
}

/**
 * Plant/facility entity representing operational locations.
 * Mirrors backend Plant model schema.
 */
export interface Plant {
  id?: number;
  name: string;
  code?: string;
  supplier?: number | string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  phone?: string;
  manager?: string;
  plant_type?: 'processing' | 'distribution' | 'storage' | 'office' | 'other';
  capacity?: number;
  export_approved?: boolean;
  is_active?: boolean;
  created_on?: string;
  modified_on?: string;
}

/**
 * Carrier entity for logistics management.
 * Mirrors backend Carrier model schema.
 */
export interface Carrier {
  id?: number;
  name: string;
  code?: string;
  contact_person?: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  carrier_type?: 'trucking' | 'rail' | 'air' | 'ocean' | 'courier' | 'other';
  mc_number?: string;
  dot_number?: string;
  is_active?: boolean;
  created_on?: string;
  modified_on?: string;
  // FMCSA QCMobile safety/authority verification snapshot (read-only).
  fmcsa_last_verified_at?: string | null;
  fmcsa_allowed_to_operate?: boolean | null;
  fmcsa_out_of_service?: boolean | null;
  fmcsa_out_of_service_date?: string | null;
  fmcsa_safety_snapshot?: Record<string, unknown> | null;
  logo?: string | null;
  logo_url?: string | null;
}

// ─── HTTP Client Adapter ──────────────────────────────────────────────────

/**
 * Adapter that satisfies EntityHttpClient interface.
 * This is a stub — consumers will inject the real Axios/fetch client
 * via the module initialization in frontend/src/services/businessApi.ts
 */
export interface IBusinessHttpClient {
  get<T>(path: string, params?: Record<string, unknown>): Promise<T>;
  post<T>(path: string, data?: unknown): Promise<T>;
  patch<T>(path: string, data?: unknown): Promise<T>;
  put<T>(path: string, data?: unknown): Promise<T>;
  delete(path: string): Promise<void>;
}

/**
 * Lazy-evaluated HTTP client. In the browser, this is set by the frontend
 * services module. In tests, it can be mocked.
 */
let httpClient: IBusinessHttpClient | null = null;

/**
 * Set the HTTP client implementation (called during app initialization).
 * Frontend: called from frontend/src/services/businessApi.ts with Axios adapter
 * Tests: can be mocked for testing without network calls
 */
export function setBusinessHttpClient(client: IBusinessHttpClient): void {
  httpClient = client;
}

/**
 * Get the configured HTTP client (throws if not initialized).
 */
function getHttpClient(): IBusinessHttpClient {
  if (!httpClient) {
    throw new Error(
      'BusinessApi HTTP client not initialized. ' +
        'Call setBusinessHttpClient() during app startup.'
    );
  }
  return httpClient;
}

// ─── Unified Business API Endpoints ──────────────────────────────────────

/**
 * Typed CRUD interface for customers.
 * All requests automatically include tenant context via HTTP interceptors.
 * Errors are normalized to structured { code, message, details } format.
 */
export function getCustomersApi(): EntityService<Customer> {
  return createEntityService<Customer>(getHttpClient(), 'customers');
}

/**
 * Typed CRUD interface for suppliers.
 * All requests automatically include tenant context via HTTP interceptors.
 * Errors are normalized to structured { code, message, details } format.
 */
export function getSuppliersApi(): EntityService<Supplier> {
  return createEntityService<Supplier>(getHttpClient(), 'suppliers');
}

/**
 * Typed CRUD interface for contacts.
 * All requests automatically include tenant context via HTTP interceptors.
 * Errors are normalized to structured { code, message, details } format.
 */
export function getContactsApi(): EntityService<Contact> {
  return createEntityService<Contact>(getHttpClient(), 'contacts');
}

/**
 * Typed CRUD interface for purchase orders.
 * All requests automatically include tenant context via HTTP interceptors.
 * Errors are normalized to structured { code, message, details } format.
 */
export function getPurchaseOrdersApi(): EntityService<PurchaseOrder> {
  return createEntityService<PurchaseOrder>(getHttpClient(), 'purchase-orders');
}

/**
 * Typed CRUD interface for accounts receivable.
 * All requests automatically include tenant context via HTTP interceptors.
 * Errors are normalized to structured { code, message, details } format.
 */
export function getAccountsReceivableApi(): EntityService<AccountsReceivable> {
  return createEntityService<AccountsReceivable>(getHttpClient(), 'accounts-receivable');
}

/**
 * Typed CRUD interface for plants/facilities.
 * All requests automatically include tenant context via HTTP interceptors.
 * Errors are normalized to structured { code, message, details } format.
 */
export function getPlantsApi(): EntityService<Plant> {
  return createEntityService<Plant>(getHttpClient(), 'plants');
}

/**
 * Typed CRUD interface for carriers.
 * All requests automatically include tenant context via HTTP interceptors.
 * Errors are normalized to structured { code, message, details } format.
 */
export function getCarriersApi(): EntityService<Carrier> {
  return createEntityService<Carrier>(getHttpClient(), 'carriers');
}

/**
 * FMCSA verification response shape returned by POST /carriers/{id}/verify-fmcsa/.
 */
export interface CarrierFmcsaVerifyResponse extends Partial<Carrier> {
  configured?: boolean;
  found?: boolean;
  detail?: string;
}

/**
 * Trigger a live FMCSA QCMobile safety/authority lookup for a carrier and
 * persist the resulting snapshot. Soft-fails (200 response, configured:false)
 * when FMCSA_WEBKEY is not set on the backend — never throws for that case.
 */
export function verifyCarrierFmcsa(
  id: number | string,
  overrides?: { dot_number?: string; mc_number?: string }
): Promise<CarrierFmcsaVerifyResponse> {
  return getHttpClient().post<CarrierFmcsaVerifyResponse>(`carriers/${id}/verify-fmcsa/`, overrides);
}

/**
 * A single origin→destination lane summary returned by GET /carriers/{id}/lanes/.
 * `count`/`latest` are only present on `top_lanes` entries; `id`/`created_on`/
 * `freight_rate` are only present on `recent_lanes` entries.
 */
export interface CarrierLaneSummary {
  origin: string;
  destination: string;
  id?: number | string;
  created_on?: string | null;
  freight_rate?: number | string | null;
  count?: number;
  latest?: string | null;
}

export interface CarrierLanesResponse {
  recent_lanes: CarrierLaneSummary[];
  top_lanes: CarrierLaneSummary[];
}

/**
 * Recent + top-volume (ranked by Carrier PO count, not $/lbs) origin→destination
 * lanes for a carrier, backing the "Recent Lanes" / "Top 5 Volume Lanes" boxes
 * on the Carrier record page.
 */
export function getCarrierLanes(id: number | string): Promise<CarrierLanesResponse> {
  return getHttpClient().get<CarrierLanesResponse>(`carriers/${id}/lanes/`);
}

// ─── Marketplace Types ──────────────────────────────────────────────────

export type ListingStatus = 'draft' | 'pending_approval' | 'active' | 'sold' | 'expired' | 'rejected';

export interface MarketplaceListing {
  id?: number;
  product_name: string;
  description?: string;
  protein_type?: string;
  protein_type_display?: string;
  unit_price: number | string;
  quantity: number | string;
  unit?: string;
  unit_display?: string;
  minimum_order_quantity?: number | string | null;
  availability_date: string; // ISO date
  expiry_date?: string | null;
  seller_name?: string;
  seller_display_name?: string;
  seller_location?: string;
  status?: ListingStatus;
  is_approved?: boolean;
  is_flagged?: boolean;
  rejection_reason?: string;
  created_on?: string;
}

export interface MarketplaceListingFilters {
  q?: string;
  protein_type?: string;
  price_min?: number | string;
  price_max?: number | string;
  available_from?: string; // ISO date
  state?: string; // US state abbreviation or full name (e.g., 'WI', 'Wisconsin')
  page?: number;
  page_size?: number;
}

export interface ListingInquiryPayload {
  buyer_name?: string;
  buyer_email?: string;
  buyer_company?: string;
  message?: string;
  requested_quantity?: number | string;
}

export interface ListingInquiryResponse {
  id: number;
  status: 'submitted';
  listing_id: number;
  message: string;
}

export function getMarketplaceApi() {
  const client = getHttpClient();
  return {
    listPublic(filters?: MarketplaceListingFilters): Promise<PaginatedResponse<MarketplaceListing>> {
      return client.get<PaginatedResponse<MarketplaceListing>>('marketplace/listings/', filters as Record<string, unknown>);
    },
    get(id: number): Promise<MarketplaceListing> {
      return client.get<MarketplaceListing>(`marketplace/listings/${id}/`);
    },
    create(data: MarketplaceListing): Promise<MarketplaceListing> {
      return client.post<MarketplaceListing>('marketplace/listings/', data);
    },
    update(id: number, data: Partial<MarketplaceListing>): Promise<MarketplaceListing> {
      return client.patch<MarketplaceListing>(`marketplace/listings/${id}/`, data);
    },
    inquire(id: number, payload: ListingInquiryPayload): Promise<ListingInquiryResponse> {
      return client.post<ListingInquiryResponse>(`marketplace/listings/${id}/inquire/`, payload);
    },
  };
}

// ─── Re-export Common Types ──────────────────────────────────────────────

/**
 * Paginated list response (mirrors DRF PageNumberPagination).
 * Returned by all .list() operations.
 */
export type { PaginatedResponse };

/**
 * CRUD service interface for type checking.
 * All entity APIs conform to this shape.
 */
export type { EntityService };

// ─── Convenience Namespace ───────────────────────────────────────────────

/**
 * Single entry point for all business API operations.
 * Memoized getters ensure referential stability for React hooks.
 */
export const businessApi = {
  /**
   * Customers CRUD API.
   * @example
   *   const customers = await businessApi.customers.list({ page: 1 });
   *   const customer = await businessApi.customers.get(42);
   *   const newCustomer = await businessApi.customers.create({ name: 'ACME' });
   */
  get customers() {
    return getCustomersApi();
  },

  /**
   * Suppliers CRUD API.
   * @example
   *   const suppliers = await businessApi.suppliers.list();
   *   const supplier = await businessApi.suppliers.get(id);
   *   const updated = await businessApi.suppliers.patch(id, { is_active: false });
   */
  get suppliers() {
    return getSuppliersApi();
  },

  /**
   * Contacts CRUD API.
   * @example
   *   const contacts = await businessApi.contacts.list();
   *   const contact = await businessApi.contacts.create({ name, email });
   */
  get contacts() {
    return getContactsApi();
  },

  /**
   * Purchase orders CRUD API.
   * @example
   *   const orders = await businessApi.purchaseOrders.list({ status: 'pending' });
   *   const order = await businessApi.purchaseOrders.update(id, { status: 'approved' });
   */
  get purchaseOrders() {
    return getPurchaseOrdersApi();
  },

  /**
   * Accounts receivable CRUD API.
   * @example
   *   const invoices = await businessApi.accountsReceivable.list();
   *   const invoice = await businessApi.accountsReceivable.get(id);
   */
  get accountsReceivable() {
    return getAccountsReceivableApi();
  },

  /**
   * Plants/facilities CRUD API.
   * @example
   *   const plants = await businessApi.plants.list();
   *   const plant = await businessApi.plants.create({ name, supplier });
   */
  get plants() {
    return getPlantsApi();
  },

  /**
   * Carriers CRUD API.
   * @example
   *   const carriers = await businessApi.carriers.list();
   *   const carrier = await businessApi.carriers.patch(id, { is_active: true });
   */
  get carriers() {
    return getCarriersApi();
  },

  /**
   * Marketplace listings API (public browse + operator create + buyer RFQ).
   * @example
   *   const listings = await businessApi.marketplace.listPublic({ protein_type: 'beef' });
   *   await businessApi.marketplace.inquire(listingId, { buyer_email: '...', message: '...' });
   */
  get marketplace() {
    return getMarketplaceApi();
  },
} as const;

// ─── Default Export ──────────────────────────────────────────────────────

export default businessApi;
